/**
 * MKS-447 (part 6) — the admin-facing proof that the Linear mirror is complete.
 *
 * One row per Linear issue with a per-field status, and a drill-down showing,
 * field by field, what Linear has and what Monksheets has. Read-only end to
 * end: the endpoint behind it runs the sync's own differ in dry-run mode and
 * writes nothing, so nothing on this page can change a task.
 *
 * The batch comes from the operator, pasted as the JSON n8n assembles from the
 * Linear API, because that is the only place a Linear issue payload exists —
 * Monksheets never stores one. Keeping the input a paste rather than a
 * Linear-API call from the server is also what keeps this page from needing
 * Linear credentials of its own.
 *
 * Shaped after `TmConversionReadinessPage`: the same table idiom, the same
 * expand-a-row drill-down.
 */
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../../components/common/Spinner';
import { useLinearReconciliation } from '../../hooks/useLinearReconciliation';
import {
  MAX_RECONCILE_ISSUES,
  type LinearFieldStatus,
  type LinearIssueReconciliation,
} from '../../services/api/linearReconciliation';
import { toErrorMessage } from '../../utils/toErrorMessage';

/** The label and the colour each status reads as. */
const STATUS_LABEL: Record<LinearFieldStatus, string> = {
  matched: 'matched',
  differing: 'differs',
  missing_in_monksheets: 'missing here',
  monksheets_only: 'only here',
};

const STATUS_CLASS: Record<LinearFieldStatus, string> = {
  matched: 'bg-green-50 text-green-800 border-green-200',
  differing: 'bg-amber-50 text-amber-800 border-amber-200',
  missing_in_monksheets: 'bg-red-50 text-red-800 border-red-200',
  monksheets_only: 'bg-blue-50 text-blue-800 border-blue-200',
};

/** The task's vocabulary, rendered in a person's. */
const FIELD_LABEL: Record<string, string> = {
  title: 'Title',
  status: 'Status',
  description: 'Description',
  assignee: 'Assignee',
  priority: 'Priority',
  dueDate: 'Due date',
  labels: 'Labels',
  comments: 'Comments',
  attachments: 'Files',
  links: 'Links',
  // MKS-469 — only sent while label routing is on.
  project: 'Project',
};

function StatusBadge({ status }: { status: LinearFieldStatus }) {
  return (
    <span
      className={`inline-block rounded border px-2 py-0.5 text-xs ${STATUS_CLASS[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

/**
 * The paste, turned into the array the endpoint takes.
 *
 * Both spellings are accepted because both are what an operator actually has
 * in the clipboard: a bare array out of a Linear export, or the
 * `{ "issues": [...] }` envelope the endpoint itself documents. A single issue
 * object is wrapped, so pasting one payload straight out of the sync endpoint's
 * logs works too.
 */
export function parseIssueBatch(raw: string): { issues: unknown[] } | { error: string } {
  const text = raw.trim();
  if (text === '') return { error: 'Paste the Linear issue payloads first.' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    return { error: `That is not valid JSON: ${err instanceof Error ? err.message : 'parse failed'}` };
  }

  const issues = Array.isArray(parsed)
    ? parsed
    : parsed && typeof parsed === 'object' && Array.isArray((parsed as { issues?: unknown }).issues)
      ? ((parsed as { issues: unknown[] }).issues)
      : parsed && typeof parsed === 'object'
        ? [parsed]
        : null;

  if (issues === null) {
    return { error: 'Expected a Linear issue, an array of them, or { "issues": [ … ] }.' };
  }
  if (issues.length === 0) return { error: 'The batch is empty.' };
  if (issues.length > MAX_RECONCILE_ISSUES) {
    return {
      error: `${issues.length} issues: the report takes at most ${MAX_RECONCILE_ISSUES} at a time.`,
    };
  }
  return { issues };
}

function IssueDetail({ issue }: { issue: LinearIssueReconciliation }) {
  if (issue.error) {
    return <p className="text-sm text-red-800">{issue.error}</p>;
  }
  return (
    <table className="min-w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase text-gray-500">
          <th className="py-1 pr-4 font-medium">Field</th>
          <th className="py-1 pr-4 font-medium">Linear</th>
          <th className="py-1 pr-4 font-medium">Monksheets</th>
          <th className="py-1 font-medium">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {issue.fields.map((field) => (
          <tr key={field.field} className="align-top">
            <td className="py-2 pr-4 whitespace-nowrap text-gray-700">
              {FIELD_LABEL[field.field] ?? field.field}
            </td>
            <td className="py-2 pr-4 text-gray-900 whitespace-pre-wrap break-words">
              {field.linear ?? <span className="text-gray-400">—</span>}
            </td>
            <td className="py-2 pr-4 text-gray-900 whitespace-pre-wrap break-words">
              {field.monksheets ?? <span className="text-gray-400">—</span>}
            </td>
            <td className="py-2 whitespace-nowrap">
              <StatusBadge status={field.status} />
              {field.detail && (
                <p className="mt-1 text-xs text-gray-600 whitespace-normal">{field.detail}</p>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function LinearReconciliationPage() {
  const [raw, setRaw] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const reconcile = useLinearReconciliation();

  const report = reconcile.data ?? null;

  const worstFirst = useMemo(() => {
    if (!report) return [];
    const order: Record<string, number> = {
      missing_in_monksheets: 0,
      differing: 1,
      monksheets_only: 2,
      matched: 3,
    };
    return [...report.issues].sort(
      (a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9),
    );
  }, [report]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const run = () => {
    const parsed = parseIssueBatch(raw);
    if ('error' in parsed) {
      setParseError(parsed.error);
      return;
    }
    setParseError(null);
    setExpanded(new Set());
    reconcile.mutate(parsed.issues);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Linear ↔ Monksheets mirror</h1>
        <p className="mt-1 text-sm text-gray-600">
          Field by field, what Linear has and what Monksheets has. The comparison runs the
          mirror&apos;s own differ in dry-run mode, so it can never disagree with what a real
          sync would do — and it writes nothing.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <label htmlFor="linear-batch" className="block text-sm font-medium text-gray-700">
          Linear issue payloads
        </label>
        <p className="text-xs text-gray-600">
          The JSON n8n posts to the sync endpoint: one issue, an array of them, or{' '}
          <code>{'{ "issues": [ … ] }'}</code>. At most {MAX_RECONCILE_ISSUES} at a time.
        </p>
        <textarea
          id="linear-batch"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={8}
          spellCheck={false}
          className="w-full rounded border border-gray-300 p-2 font-mono text-xs"
          placeholder='[{ "id": "…", "identifier": "SCR-1", "team": { "key": "SCR" }, "title": "…" }]'
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={run}
            disabled={reconcile.isPending}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {reconcile.isPending ? 'Comparing…' : 'Compare'}
          </button>
          {reconcile.isPending && <Spinner />}
        </div>

        {parseError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{parseError}</p>
          </div>
        )}

        {reconcile.isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">
              {toErrorMessage(reconcile.error, 'Could not run the comparison.')}
            </p>
          </div>
        )}
      </div>

      {report && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            {report.counts.matched} of {report.counts.total} fully mirrored —{' '}
            {report.counts.missingInMonksheets} missing in Monksheets,{' '}
            {report.counts.differing} differing, {report.counts.monksheetsOnly} only in
            Monksheets, {report.counts.failed} could not be compared.
          </p>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Issue
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Task
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Fields
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {worstFirst.map((issue) => (
                  <Fragment key={issue.linearIssueId}>
                    <tr
                      className="hover:bg-gray-50 align-top cursor-pointer"
                      onClick={() => toggle(issue.linearIssueId)}
                    >
                      <td className="px-4 py-3 text-sm">
                        <button
                          type="button"
                          className="text-blue-700 underline"
                          aria-expanded={expanded.has(issue.linearIssueId)}
                        >
                          {issue.identifier}
                        </button>
                        <p className="text-xs text-gray-600">{issue.title}</p>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {issue.taskId === null ? (
                          <span className="text-gray-500">none</span>
                        ) : (
                          <Link
                            to={`/projects/${issue.projectId}/board?openTask=${issue.taskId}`}
                            className="text-blue-700 underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            #{issue.taskId}
                          </Link>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {issue.error ? (
                          <span className="text-xs text-red-800">{issue.error}</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {issue.fields
                              .filter((f) => f.status !== 'matched')
                              .map((f) => (
                                <span
                                  key={f.field}
                                  className={`inline-block rounded border px-2 py-0.5 text-xs ${STATUS_CLASS[f.status]}`}
                                >
                                  {FIELD_LABEL[f.field] ?? f.field}
                                </span>
                              ))}
                            {issue.fields.every((f) => f.status === 'matched') && (
                              <span className="text-xs text-gray-500">everything matches</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <StatusBadge status={issue.status} />
                      </td>
                    </tr>
                    {expanded.has(issue.linearIssueId) && (
                      <tr className="bg-gray-50">
                        <td colSpan={4} className="px-4 py-3">
                          <IssueDetail issue={issue} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
